import React from 'react';
import { connect } from 'react-redux';
import { Tabs } from '@brasshq/ui-kit';
import useFilter from 'src/hooks/useFilter';
import { ATFetchCommons, fetchRoles } from 'src/modules/common';
import { CommonContext } from 'src/modules/common/context';
import { withErrorBoundary } from 'src/sharedComponents/ErrorBoundary';
import SpanBadge from 'src/sharedComponents/SpanBadge';
import { HandlePermission, ReduxStore } from 'src/types';

import TeamMatesTable from '../../components/TeamMatesTable';
import { SENT_INVITE_DEFAULT_FILTERS } from '../../data';
import * as actions from '../../redux/actions';
import { RDTTeammatesTabFetchParams } from '../../types';
import { getIsPermitted } from '../../utils';

import styles from './TeamMatesPage.module.scss';

interface TeamMatesPageProps {
  teamMembers: ReduxStore['module/administration']['teamMembers'];
  sentInvites: ReduxStore['module/administration']['sentInvites'];
  roles: ReduxStore['module/common']['roles'];
  fetchTeamMembers: typeof actions.fetchTeamMembers;
  fetchSentInvites: typeof actions.fetchSentInvites;
  fetchRoles: ATFetchCommons;
}

const TeamMatesPage: React.FC<TeamMatesPageProps> = props => {
  const {
    teamMembers,
    sentInvites,
    fetchTeamMembers,
    fetchSentInvites,
    fetchRoles,
    roles: { data: roles },
  } = props;

  const { profile, hasSessionTimedOut } = React.useContext(CommonContext);

  const [state, setState] = React.useState({
    activeIndex: 0,
  });

  const userPermissions = profile?.permissions || [];

  const handleFetch = (params: RDTTeammatesTabFetchParams) => {
    switch (state.activeIndex) {
      case 0:
        return fetchTeamMembers(params);
      case 1:
        return fetchSentInvites({
          ...SENT_INVITE_DEFAULT_FILTERS,
          ...params,
        });
      default:
        return;
    }
  };

  const { updateFilters, filterData } = useFilter<RDTTeammatesTabFetchParams>(
    { limit: 10, page: 1 },
    params => !hasSessionTimedOut && handleFetch(params),
    [state.activeIndex, hasSessionTimedOut],
  );

  const handleOptionVisibility: HandlePermission = (
    permissions,
    strict = false,
  ) => {
    return getIsPermitted(userPermissions, permissions, strict);
  };

  const isPermittedToViewInvites = handleOptionVisibility('team.manage');

  const handleNavigation = (activeIndex: number) =>
    setState(state => ({ ...state, activeIndex }));

  const renderTabLabel = (label: string, count: number) => (
    <p className={styles.TeamMatesPage_tab_label}>
      {label}
      <SpanBadge>{count}</SpanBadge>
    </p>
  );

  const tabConfig = [
    {
      label: renderTabLabel('People', teamMembers?.pagination?.total || 0),
      component: (
        <TeamMatesTable
          activeTabIndex={state.activeIndex}
          data={teamMembers.data}
          pagination={teamMembers.pagination!}
          loading={!!teamMembers.loading}
          roles={roles}
          refresh={filterData}
          paginate={updateFilters}
          handlePermissions={handleOptionVisibility}
        />
      ),
      isVisible: true,
    },
    {
      label: renderTabLabel(
        'Sent Invites',
        sentInvites?.pagination?.total || 0,
      ),
      component: (
        <TeamMatesTable
          activeTabIndex={state.activeIndex}
          data={sentInvites.data}
          pagination={sentInvites.pagination!}
          loading={!!sentInvites.loading}
          roles={roles}
          refresh={filterData}
          paginate={updateFilters}
          handlePermissions={handleOptionVisibility}
        />
      ),
      isVisible: isPermittedToViewInvites,
    },
  ];

  React.useEffect(() => {
    fetchRoles();
    isPermittedToViewInvites &&
      fetchSentInvites(
        SENT_INVITE_DEFAULT_FILTERS as RDTTeammatesTabFetchParams,
      );
  }, []);

  return (
    <section className={styles.TeamMatesPage}>
      <h3 className={styles.TeamMatesPage_ttl}>Team mates</h3>
      <Tabs
        className={styles.TeamMatesPage_tab}
        activeIndex={state.activeIndex}
        config={tabConfig}
        onNavigate={handleNavigation}
        hideHeaderForSingleVisibleTab
      />
    </section>
  );
};

const mapStateToProps = (state: ReduxStore) => {
  const { teamMembers, sentInvites } = state['module/administration'];
  const { roles } = state['module/common'];

  return {
    sentInvites,
    teamMembers,
    roles,
  };
};

const mapDispatchToProps = {
  ...actions,
  fetchRoles,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withErrorBoundary(TeamMatesPage, { useBackButton: true, forRoute: true }));
